import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Chip,
  FormControlLabel,
  Switch,
  InputLabel,
  FormControl,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import Header from "../../../components/Header";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";
import {
  createDashboardMessage,
  updateDashboardMessage,
} from "../../../data/DashboardNotifications/dashboardMessagesApi";
import { roleManagemenUrl } from "../../../config";
import {
  CATEGORY_OPTIONS,
  DISPLAY_TYPE_OPTIONS,
} from "../../../data/DashboardNotifications/notificationStyles";

// datetime-local inputs need "YYYY-MM-DDTHH:mm" in local time; the API
// deals in ISO timestamps, so values are converted at the form boundary.
const toDateTimeLocal = (isoValue) => {
  if (!isoValue) return "";
  const date = new Date(isoValue);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const toIsoOrNull = (dateTimeLocalValue) => {
  if (!dateTimeLocalValue) return null;
  const date = new Date(dateTimeLocalValue);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const DashboardMessageForm = (props) => {
  const [roleGroups, setRoleGroups] = useState([]);
  const [roleGroupsLoading, setRoleGroupsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const response = await fetchJsonDynamic(
          "allRoleGroups",
          null,
          null,
          roleManagemenUrl.uri
        );
        const results = response?.results ? JSON.parse(response.results) : [];
        if (isMounted) setRoleGroups(Array.isArray(results) ? results : []);
      } catch (error) {
        console.error("Error loading role groups:", error);
      } finally {
        if (isMounted) setRoleGroupsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  const initialValues = {
    title: props.data ? props.data.title : "",
    body: props.data ? props.data.body : "",
    category: props.data ? props.data.category : "general",
    displayType: props.data ? props.data.displayType : "card",
    priority: props.data ? props.data.priority : 0,
    targetRoleGroup: props.data?.targetRoleGroup
      ? props.data.targetRoleGroup.map(String)
      : [],
    isDismissible: props.data ? !!props.data.isDismissible : true,
    startsAt: props.data
      ? toDateTimeLocal(props.data.startsAt)
      : toDateTimeLocal(new Date().toISOString()),
    expiresAt: props.data ? toDateTimeLocal(props.data.expiresAt) : "",
    ctaLabel: props.data ? props.data.ctaLabel || "" : "",
    ctaUrl: props.data ? props.data.ctaUrl || "" : "",
  };

  const validationSchema = yup.object().shape({
    title: yup.string().required("Title is required"),
    body: yup.string().required("Body is required"),
    category: yup.string().required("Category is required"),
    displayType: yup.string().required("Display type is required"),
    priority: yup
      .number()
      .typeError("Priority must be a number")
      .integer("Priority must be a whole number")
      .min(0, "Priority cannot be negative")
      .required("Priority is required"),
    startsAt: yup.string().required("Start date is required"),
    expiresAt: yup
      .string()
      .nullable()
      .test(
        "after-start",
        "Expiry must be after the start date",
        function (value) {
          if (!value) return true;
          return new Date(value) > new Date(this.parent.startsAt);
        }
      ),
    ctaUrl: yup
      .string()
      .nullable()
      .transform((value, originalValue) =>
        originalValue?.trim() === "" ? null : value
      )
      .url("CTA URL must be a valid URL"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const variables = {
        title: values.title,
        body: values.body,
        category: values.category,
        displayType: values.displayType,
        priority: Number(values.priority),
        targetRoleGroup: values.targetRoleGroup,
        isDismissible: values.isDismissible,
        startsAt: toIsoOrNull(values.startsAt),
        expiresAt: toIsoOrNull(values.expiresAt),
        ctaLabel: values.ctaLabel || null,
        ctaUrl: values.ctaUrl || null,
      };
      if (props.isEditing) variables.id = props.data.id;

      const result = props.isEditing
        ? await updateDashboardMessage(variables)
        : await createDashboardMessage(variables);

      if (result) {
        props.onClose();
        props.onAction();
        await Swal.fire(
          "Success!",
          `Dashboard message has been ${
            props.isEditing ? "updated" : "created"
          } successfully`,
          "success"
        );
      } else {
        Swal.fire("Error!", "Something went wrong", "error");
      }
    } catch (error) {
      console.error("Error:", error.message);
      Swal.fire("Error!", error.message || "Please contact the Admin.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Dashboard Message" : "Create Dashboard Message"}
        subtitle={
          props.isEditing
            ? "Edit an existing dashboard message"
            : "Publish a new message to the user dashboard"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="20px">
              <TextField
                fullWidth
                variant="filled"
                label="Title*"
                name="title"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.title}
                error={touched.title && !!errors.title}
                helperText={touched.title && errors.title}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                variant="filled"
                label="Body*"
                name="body"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.body}
                error={touched.body && !!errors.body}
                helperText={touched.body && errors.body}
              />

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="20px">
                <FormControl variant="filled" fullWidth>
                  <InputLabel id="category-label">Category*</InputLabel>
                  <Select
                    labelId="category-label"
                    name="category"
                    value={values.category}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {CATEGORY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl variant="filled" fullWidth>
                  <InputLabel id="display-type-label">Display Type*</InputLabel>
                  <Select
                    labelId="display-type-label"
                    name="displayType"
                    value={values.displayType}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {DISPLAY_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="20px">
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Priority (100+ is critical)*"
                  name="priority"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.priority}
                  error={touched.priority && !!errors.priority}
                  helperText={touched.priority && errors.priority}
                />

                <FormControl variant="filled" fullWidth>
                  <InputLabel id="target-role-group-label">
                    Target Audience
                  </InputLabel>
                  <Select
                    labelId="target-role-group-label"
                    multiple
                    disabled={roleGroupsLoading}
                    value={values.targetRoleGroup}
                    onChange={(e) =>
                      setFieldValue("targetRoleGroup", e.target.value)
                    }
                    input={<OutlinedInput label="Target Audience" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((id) => {
                          const group = roleGroups.find(
                            (g) => String(g.Id) === String(id)
                          );
                          return (
                            <Chip
                              key={id}
                              size="small"
                              label={group ? group.RoleGroup : id}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {roleGroups.map((group) => (
                      <MenuItem key={group.Id} value={String(group.Id)}>
                        <Checkbox
                          checked={values.targetRoleGroup.includes(
                            String(group.Id)
                          )}
                        />
                        <ListItemText primary={group.RoleGroup} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="20px">
                <TextField
                  fullWidth
                  variant="filled"
                  type="datetime-local"
                  label="Starts At*"
                  name="startsAt"
                  InputLabelProps={{ shrink: true }}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.startsAt}
                  error={touched.startsAt && !!errors.startsAt}
                  helperText={touched.startsAt && errors.startsAt}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="datetime-local"
                  label="Expires At"
                  name="expiresAt"
                  InputLabelProps={{ shrink: true }}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.expiresAt}
                  error={touched.expiresAt && !!errors.expiresAt}
                  helperText={
                    (touched.expiresAt && errors.expiresAt) ||
                    "Leave blank for a message that never expires"
                  }
                />
              </Box>

              <Box display="grid" gridTemplateColumns="1fr 1fr" gap="20px">
                <TextField
                  fullWidth
                  variant="filled"
                  label="CTA Label"
                  name="ctaLabel"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.ctaLabel}
                  error={touched.ctaLabel && !!errors.ctaLabel}
                  helperText={touched.ctaLabel && errors.ctaLabel}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  label="CTA URL"
                  name="ctaUrl"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.ctaUrl}
                  error={touched.ctaUrl && !!errors.ctaUrl}
                  helperText={touched.ctaUrl && errors.ctaUrl}
                />
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    name="isDismissible"
                    checked={values.isDismissible}
                    onChange={(e) =>
                      setFieldValue("isDismissible", e.target.checked)
                    }
                  />
                }
                label="Dismissible by user"
              />
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default DashboardMessageForm;
